import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Plus, Trash, PencilSimple, Check, X, Tag, Warning, Calculator } from '@phosphor-icons/react';

/**
 * Beheer prijsgroepen + actieve Kassa-prijsgroep.
 * Gebruikt /api/artikel-params als storage: {groepen:[{key,label,color}], kassa_actieve_groep}
 */
export default function KassaPrijsgroepenPanel() {
  const [params, setParams] = useState(null);
  const [editIdx, setEditIdx] = useState(null);
  const [draft, setDraft] = useState({ key: '', label: '', color: '#3B82F6' });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [herrekenOpen, setHerrekenOpen] = useState(false);
  const [hrBron, setHrBron] = useState('basic');
  const [hrDoel, setHrDoel] = useState('kassa');
  const [hrProfMult, setHrProfMult] = useState(1.30);
  const [hrRestMult, setHrRestMult] = useState(2.00);
  const [hrBusy, setHrBusy] = useState(false);

  const load = () => {
    API.get('/artikel-params').then(r => setParams(r.data || {})).catch(() => setParams({}));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  const groepen = params?.groepen || [];
  const actief = params?.kassa_actieve_groep || 'basic';

  const saveAll = async (updated) => {
    setBusy(true);
    try {
      await API.put('/artikel-params', updated);
      setParams(updated);
      setToast({ text: 'Opgeslagen', kind: 'ok' });
    } catch (e) {
      setToast({ text: e.response?.data?.detail || 'Opslaan mislukt', kind: 'err' });
    } finally { setBusy(false); }
  };

  const setActief = (key) => {
    saveAll({ ...params, kassa_actieve_groep: key });
  };

  const startAdd = () => {
    setEditIdx('new');
    setDraft({ key: '', label: '', color: '#3B82F6' });
  };

  const startEdit = (idx) => {
    setEditIdx(idx);
    setDraft({ ...groepen[idx] });
  };

  const cancelEdit = () => {
    setEditIdx(null);
    setDraft({ key: '', label: '', color: '#3B82F6' });
  };

  const saveDraft = async () => {
    const key = (draft.key || '').trim().toLowerCase().replace(/\s+/g, '_');
    const label = (draft.label || '').trim();
    if (!key || !label) { setToast({ text: 'Sleutel en label zijn verplicht', kind: 'warn' }); return; }
    if (!/^[a-z0-9_]+$/.test(key)) { setToast({ text: 'Sleutel mag enkel a-z, 0-9, _', kind: 'warn' }); return; }
    let nieuwe;
    if (editIdx === 'new') {
      if (groepen.some(g => g.key === key)) { setToast({ text: `Sleutel "${key}" bestaat al`, kind: 'warn' }); return; }
      nieuwe = [...groepen, { key, label, color: draft.color }];
    } else {
      nieuwe = groepen.map((g, i) => i === editIdx ? { key, label, color: draft.color } : g);
      // Als de actieve groep zijn key gewijzigd is → actief ook updaten
      if (groepen[editIdx].key === actief && groepen[editIdx].key !== key) {
        await saveAll({ ...params, groepen: nieuwe, kassa_actieve_groep: key });
        setEditIdx(null);
        return;
      }
    }
    await saveAll({ ...params, groepen: nieuwe });
    setEditIdx(null);
  };

  const remove = async (idx) => {
    const g = groepen[idx];
    if (groepen.length <= 1) { setToast({ text: 'Minstens 1 prijsgroep vereist', kind: 'warn' }); return; }
    if (!window.confirm(`Prijsgroep "${g.label}" verwijderen? Artikel-prijzen onder deze groep blijven bewaard maar zijn niet meer selecteerbaar.`)) return;
    const nieuwe = groepen.filter((_, i) => i !== idx);
    const payload = { ...params, groepen: nieuwe };
    if (actief === g.key) payload.kassa_actieve_groep = nieuwe[0].key;
    await saveAll(payload);
  };

  if (!params) return <div className="text-sm text-gray-500 py-6 text-center">Laden...</div>;

  const doeKassaHerreken = async () => {
    const p = parseFloat(hrProfMult) || 0;
    const r = parseFloat(hrRestMult) || 0;
    if (p <= 0 || r <= 0) { setToast({ text: 'Multipliers moeten > 0 zijn', kind: 'warn' }); return; }
    if (hrBron === hrDoel) { setToast({ text: 'Bron- en doel-prijsgroep mogen niet gelijk zijn', kind: 'warn' }); return; }
    const profPct = ((p - 1) * 100).toFixed(0);
    const restPct = ((r - 1) * 100).toFixed(0);
    const vraag =
      `Kassa-prijzen herrekenen op basis van "${hrBron}"?\n\n` +
      `  • Profielen: ${hrBron} × ${p.toFixed(2)} (${p >= 1 ? '+' : ''}${profPct}%)\n` +
      `  • Rest: ${hrBron} × ${r.toFixed(2)} (${r >= 1 ? '+' : ''}${restPct}%)\n\n` +
      `Doel-prijsgroep "${hrDoel}" wordt overschreven voor alle artikels.`;
    if (!window.confirm(vraag)) return;
    setHrBusy(true);
    try {
      const res = await API.post('/artikels/kassa-herrekenen', {
        bron_groep: hrBron,
        doel_groep: hrDoel,
        profiel_mult: p,
        rest_mult: r,
      });
      const d = res.data;
      setToast({
        text: `✓ ${d.profielen_bijgewerkt} profielen + ${d.overig_bijgewerkt} overige bijgewerkt${d.overgeslagen ? ` · ${d.overgeslagen} overgeslagen (geen bron-prijs)` : ''}`,
        kind: 'ok',
      });
      setHerrekenOpen(false);
    } catch (e) {
      setToast({ text: e.response?.data?.detail || 'Herrekenen mislukt', kind: 'err' });
    } finally {
      setHrBusy(false);
    }
  };

  return (
    <div data-testid="kassa-prijsgroepen-panel">
      {/* Actieve groep — grote select bovenaan */}
      <div className="mb-5 p-4 border-2 border-[#002FA7]/30 bg-[#F0F4FF]/40">
        <div className="flex items-center gap-2 mb-2">
          <Tag size={16} className="text-[#002FA7]" weight="duotone" />
          <h3 className="text-sm font-bold text-gray-900">Actieve Kassa-prijsgroep</h3>
        </div>
        <p className="text-xs text-gray-600 mb-3">
          Welke prijsgroep gebruikt de Kassa standaard voor alle artikels? U kan dit hier centraal wisselen zonder elk toestel te moeten aanpassen.
        </p>
        <div className="flex flex-wrap gap-2" data-testid="kassa-actieve-groep-opties">
          {groepen.map(g => (
            <button
              key={g.key}
              onClick={() => setActief(g.key)}
              disabled={busy}
              className={`px-4 py-2.5 text-sm font-bold border-2 transition-all ${actief === g.key ? 'text-white shadow-lg' : 'bg-white text-gray-700 hover:border-gray-400'}`}
              style={actief === g.key
                ? { background: g.color || '#002FA7', borderColor: g.color || '#002FA7' }
                : { borderColor: '#E5E7EB' }}
              data-testid={`kassa-actieve-${g.key}`}
            >
              {actief === g.key && <Check size={14} weight="bold" className="inline mr-1" />}
              {g.label}
            </button>
          ))}
        </div>
        <div className="text-[10px] text-gray-500 mt-2 italic">
          Huidige selectie: <strong className="text-gray-700 font-mono">{actief}</strong> — wijzigingen werken onmiddellijk door op de Kassa-pagina.
        </div>
      </div>

      {/* ── Bulk herreken-preset ──────────────────────────────────────── */}
      <div className="mb-5 p-4 border-2 border-[#F97316]/30 bg-[#FFF7ED]/50" data-testid="kassa-herreken-preset">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Calculator size={16} className="text-[#F97316]" weight="duotone" />
            <h3 className="text-sm font-bold text-gray-900">Kassa herrekenen — bulk-preset</h3>
          </div>
          <button
            onClick={() => setHerrekenOpen(o => !o)}
            className={`text-xs font-bold px-3 py-1 border-2 transition-colors ${herrekenOpen ? 'bg-[#F97316] text-white border-[#F97316]' : 'bg-white text-[#F97316] border-[#F97316] hover:bg-[#FFF7ED]'}`}
            data-testid="kassa-herreken-toggle"
          >
            {herrekenOpen ? 'Sluiten' : 'Openen'}
          </button>
        </div>
        <p className="text-xs text-gray-600 mb-2">
          Herbereken in één klik de volledige <strong>Kassa-prijsgroep</strong> op basis van een andere prijsgroep (bv. Basic). Handig na een bulk-prijsaanpassing.
          Profiel-categorieën krijgen een eigen multiplier; alle overige artikels een tweede.
        </p>
        {herrekenOpen && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="form-label">Bron-prijsgroep</label>
              <select className="field-input" value={hrBron} onChange={e => setHrBron(e.target.value)} data-testid="hr-bron">
                {groepen.filter(g => g.key !== hrDoel).map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Doel-prijsgroep</label>
              <select className="field-input" value={hrDoel} onChange={e => setHrDoel(e.target.value)} data-testid="hr-doel">
                {groepen.filter(g => g.key !== hrBron).map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Multiplier profielen</label>
              <input
                type="number" step="0.05" min="0.1"
                className="field-input font-mono"
                value={hrProfMult}
                onChange={e => setHrProfMult(e.target.value)}
                data-testid="hr-prof-mult"
              />
              <div className="text-[10px] text-gray-500 mt-0.5">bv. 1,30 → +30%</div>
            </div>
            <div>
              <label className="form-label">Multiplier overige</label>
              <input
                type="number" step="0.05" min="0.1"
                className="field-input font-mono"
                value={hrRestMult}
                onChange={e => setHrRestMult(e.target.value)}
                data-testid="hr-rest-mult"
              />
              <div className="text-[10px] text-gray-500 mt-0.5">bv. 2,00 → ×2</div>
            </div>
            <div className="md:col-span-4 flex items-center justify-between mt-1 pt-3 border-t border-[#F97316]/20">
              <div className="text-[11px] text-gray-600">
                Profielen = categorieën met <code className="font-mono bg-white px-1">profiel</code> in de naam + <code className="font-mono bg-white px-1">UW Horizontaal</code> (auto-detect).
              </div>
              <button
                onClick={doeKassaHerreken}
                disabled={hrBusy}
                className="btn btn-primary font-bold inline-flex items-center gap-1.5 bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-60"
                data-testid="hr-uitvoeren"
              >
                <Calculator size={14} weight="bold" /> {hrBusy ? 'Bezig...' : 'Herbereken nu'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Beheer — CRUD tabel */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-gray-900">Beheer prijsgroepen ({groepen.length})</h3>
        {editIdx === null && (
          <button onClick={startAdd} className="btn btn-primary btn-sm inline-flex items-center gap-1 font-bold" data-testid="prijsgroep-nieuw">
            <Plus size={13} /> Nieuwe prijsgroep
          </button>
        )}
      </div>

      <div className="border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600 font-bold">
            <tr>
              <th className="text-left px-3 py-2">Sleutel</th>
              <th className="text-left px-3 py-2">Label</th>
              <th className="text-left px-3 py-2">Kleur</th>
              <th className="text-right px-3 py-2">Acties</th>
            </tr>
          </thead>
          <tbody>
            {groepen.map((g, i) => (
              editIdx === i ? (
                <tr key={g.key} className="bg-[#FEF3C7]/30" data-testid={`prijsgroep-edit-row-${i}`}>
                  <td className="px-3 py-2">
                    <input className="field-input font-mono text-xs" value={draft.key} onChange={e => setDraft({ ...draft, key: e.target.value })} placeholder="bv. handel" data-testid="prijsgroep-key" />
                  </td>
                  <td className="px-3 py-2">
                    <input className="field-input" value={draft.label} onChange={e => setDraft({ ...draft, label: e.target.value })} placeholder="bv. Handelsprijs" data-testid="prijsgroep-label" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="color" value={draft.color} onChange={e => setDraft({ ...draft, color: e.target.value })} className="w-16 h-8 border border-gray-300 cursor-pointer" data-testid="prijsgroep-color" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={saveDraft} className="btn btn-primary btn-sm mr-1 inline-flex items-center gap-1" disabled={busy} data-testid="prijsgroep-save-edit">
                      <Check size={12} weight="bold" /> Opslaan
                    </button>
                    <button onClick={cancelEdit} className="btn btn-secondary btn-sm inline-flex items-center gap-1">
                      <X size={12} weight="bold" /> Annuleer
                    </button>
                  </td>
                </tr>
              ) : (
                <tr key={g.key} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`prijsgroep-rij-${g.key}`}>
                  <td className="px-3 py-2 font-mono text-xs">{g.key}</td>
                  <td className="px-3 py-2 font-bold">
                    {g.label}
                    {actief === g.key && <span className="ml-2 text-[9px] px-1.5 py-0.5 bg-[#002FA7] text-white font-bold">ACTIEF</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="inline-flex items-center gap-2">
                      <span className="inline-block w-5 h-5 border border-gray-300" style={{ background: g.color || '#002FA7' }} />
                      <span className="text-xs text-gray-600 font-mono">{g.color || '#002FA7'}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => startEdit(i)} className="p-1.5 hover:bg-gray-200 mr-1" title="Bewerken" data-testid={`prijsgroep-edit-${g.key}`}>
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={() => remove(i)}
                      className="p-1.5 hover:bg-red-100 text-red-600 disabled:opacity-30"
                      title={groepen.length <= 1 ? 'Minstens 1 groep vereist' : 'Verwijderen'}
                      disabled={groepen.length <= 1}
                      data-testid={`prijsgroep-del-${g.key}`}
                    >
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              )
            ))}
            {editIdx === 'new' && (
              <tr className="bg-[#ECFDF5]/50" data-testid="prijsgroep-new-row">
                <td className="px-3 py-2">
                  <input className="field-input font-mono text-xs" value={draft.key} onChange={e => setDraft({ ...draft, key: e.target.value })} placeholder="bv. handel" autoFocus data-testid="prijsgroep-key" />
                </td>
                <td className="px-3 py-2">
                  <input className="field-input" value={draft.label} onChange={e => setDraft({ ...draft, label: e.target.value })} placeholder="bv. Handelsprijs" data-testid="prijsgroep-label" />
                </td>
                <td className="px-3 py-2">
                  <input type="color" value={draft.color} onChange={e => setDraft({ ...draft, color: e.target.value })} className="w-16 h-8 border border-gray-300 cursor-pointer" data-testid="prijsgroep-color" />
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={saveDraft} className="btn btn-primary btn-sm mr-1 inline-flex items-center gap-1" disabled={busy} data-testid="prijsgroep-save-new">
                    <Check size={12} weight="bold" /> Toevoegen
                  </button>
                  <button onClick={cancelEdit} className="btn btn-secondary btn-sm inline-flex items-center gap-1">
                    <X size={12} weight="bold" /> Annuleer
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800 inline-flex items-start gap-2">
        <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
        <div>
          <strong>Let op:</strong> de sleutel (bv. <code className="font-mono">handel</code>) wordt gebruikt in de artikel-prijzen. Als u een nieuwe sleutel toevoegt, moet u de artikels bewerken om een prijs voor die groep in te vullen (anders gebruikt de Kassa €0). Bestaande groepen hernoemen (enkel het label) kan veilig.
        </div>
      </div>

      {toast && (
        <div
          className="fixed bottom-6 right-6 z-50 px-4 py-2.5 shadow-2xl font-bold text-sm"
          style={{ background: toast.kind === 'ok' ? '#16A34A' : toast.kind === 'warn' ? '#F59E0B' : '#DC2626', color: '#fff', minWidth: 220 }}
          data-testid="prijsgroep-toast"
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
